import type { PillarsResponse, Pillar } from "../../../types";
import { useTheme, chartColors } from "../../../theme";

interface Props {
  data: PillarsResponse;
}

const ORDER: Pillar[] = ["reading", "writing", "listening", "speaking"];
const LABEL: Record<Pillar, string> = {
  reading: "Reading",
  writing: "Writing",
  listening: "Listening",
  speaking: "Speaking",
};

// 0–10 scores rendered on a 0–100 scale to match the scorecard.
const toScore100 = (s: number) => Math.round(s * 10);

// Dedicated green for the scorecard (gauge ring, icons, bars).
const GREEN = "#4A90A4";

const ICON: Record<Pillar, JSX.Element> = {
  reading: (
    <path d="M3 5.5A1.5 1.5 0 0 1 4.5 4H10a2 2 0 0 1 2 2 2 2 0 0 1 2-2h5.5A1.5 1.5 0 0 1 21 5.5v12a1.5 1.5 0 0 1-1.5 1.5H14a2 2 0 0 0-2 2 2 2 0 0 0-2-2H4.5A1.5 1.5 0 0 1 3 17.5v-12ZM12 6v14" />
  ),
  writing: <path d="M4 20h4L19 9a2 2 0 0 0-3-3L5 17v3ZM14.5 7.5l2 2" />,
  listening: (
    <path d="M4 13a8 8 0 0 1 16 0v4a2 2 0 0 1-2 2h-1v-6h3M4 13v4a2 2 0 0 0 2 2h1v-6H4" />
  ),
  speaking: <path d="M4 5h16v11H9l-4 4v-4H4V5Z" />,
};

function balanceVerdict(scores: number[]): { title: string; note: string } {
  if (scores.length < 2) {
    return { title: "Getting started", note: "Practice across all four pillars to build your profile." };
  }
  const spread = Math.max(...scores) - Math.min(...scores);
  if (spread <= 15) {
    return { title: "Good balance", note: "Keep building consistency across all pillars." };
  }
  if (spread <= 30) {
    return { title: "Slight imbalance", note: "Lean into your weaker pillars to even things out." };
  }
  return { title: "Unbalanced", note: "Focus on your lowest pillar to level up overall." };
}

function Gauge({ value, color, track }: { value: number; color: string; track: string }) {
  const r = 52;
  const circ = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value)) / 100;
  return (
    <div className="relative h-[150px] w-[150px]">
      <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
        <circle cx="60" cy="60" r={r} fill="none" stroke={track} strokeWidth="9" />
        <circle
          cx="60"
          cy="60"
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="9"
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={circ * (1 - pct)}
          className="transition-[stroke-dashoffset] duration-700 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display font-semibold text-[2.75rem] leading-none text-ink tabular-nums">
          {Math.round(value)}
        </span>
        <span className="section-label mt-1">Overall</span>
      </div>
    </div>
  );
}

export default function PillarScorecard({ data }: Props) {
  useTheme(); // re-resolve palette on theme change
  const c = chartColors();

  const rows = ORDER.map((p) => ({
    pillar: p,
    stat: data[p],
    value: data[p]?.score != null ? toScore100(data[p]!.score!) : null,
    band: data[p]?.band ?? null,
  }));

  const present = rows.filter((r) => r.value != null) as {
    pillar: Pillar;
    stat: (typeof rows)[number]["stat"];
    value: number;
    band: string | null;
  }[];

  const overall = data.overall.score != null ? toScore100(data.overall.score) : 0;

  const verdict = balanceVerdict(present.map((r) => r.value));

  // Rank desc; pillars without data sink to the bottom.
  const ranked = [...rows].sort((a, b) => (b.value ?? -1) - (a.value ?? -1));

  return (
    <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,0.85fr)_1px_minmax(0,1.4fr)] gap-7 sm:gap-9 items-center">
      {/* Overall gauge */}
      <div className="flex flex-col items-center text-center">
        <Gauge value={overall} color={GREEN} track={c.grid} />
        {data.overall.band && (
          <span
            className="mt-3 inline-flex items-center rounded-full border border-border px-2.5 py-0.5 text-xs font-medium tabular-nums"
            style={{ color: GREEN }}
          >
            ≈ {data.overall.band}
          </span>
        )}
        <h5 className="font-display font-semibold text-ink text-lg mt-4">{verdict.title}</h5>
        <p className="text-text-tertiary text-[0.8125rem] leading-snug mt-1 max-w-[15rem]">
          {verdict.note}
        </p>
      </div>

      {/* Divider */}
      <div className="hidden sm:block self-stretch bg-border-soft" />

      {/* Ranked pillars */}
      <div className="sm:pr-6 lg:pr-10">
        <div className="flex items-center justify-between mb-5">
          <h4 className="font-display font-semibold text-ink text-lg">Four pillars</h4>
          <span className="section-label">target · {data.target}</span>
        </div>

        <div className="flex flex-col gap-4">
          {ranked.map((r, i) => {
            const has = r.value != null;
            return (
              <div key={r.pillar} className="flex items-center gap-3.5">
                <span className="w-3 text-text-tertiary text-sm tabular-nums">{i + 1}</span>
                <span
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border"
                  style={{ color: GREEN }}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-[18px] w-[18px]"
                  >
                    {ICON[r.pillar]}
                  </svg>
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-ink text-[0.9375rem]">{LABEL[r.pillar]}</span>
                    {has ? (
                      <span className="flex items-baseline gap-2 tabular-nums">
                        {r.band && (
                          <span className="text-text-tertiary text-xs font-medium">{r.band}</span>
                        )}
                        <span className="font-display font-semibold text-ink">{r.value}</span>
                      </span>
                    ) : (
                      <span className="text-text-tertiary text-sm">—</span>
                    )}
                  </div>
                  <div className="mt-1.5 h-1.5 rounded-full overflow-hidden" style={{ background: c.grid }}>
                    <div
                      className="h-full rounded-full transition-[width] duration-700 ease-out"
                      style={{
                        width: `${has ? r.value : 0}%`,
                        background: has ? GREEN : "transparent",
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-text-tertiary text-xs mt-5">
          Scores are out of 100, anchored to the CEFR scale and based on your last 10 results in each
          pillar — practise at a higher level to raise the ceiling.
        </p>
      </div>
    </div>
  );
}
